import React, { useEffect, useMemo } from 'react';

import classNames from 'classnames';
import { useDispatch } from 'react-redux';

import { useScreenMediaQuery } from '../../../../utils/use-screen-media-query';
import { Channel, LanguagesAbbr, EventNavbar, ENavbarTypes } from '../../../../types/working-model';
import { useTypedSelector } from '../../../../store/reducers/use-typed-selector';
import { getEventNavbarStyling } from "../../../live-event/utils";
import Icon, { COLORS } from '../../../general-ui/icon';
import { OptionalComponent } from '../../../../utils/optional-component';
import { useIsSessionPage, useIsSingleSessionPage } from '../../../../hooks/path.hooks';
import { MarketingNavMenu } from '../navigation/marketing-nav';
import EventLogo from '../navigation/event-logo';
import { useGetNavIconName } from './hooks/navigation-icon.hooks';
import { useNavigationVisibility } from './hooks/navigation.hooks';
import NavigationToggle from './navigation-toggle';
import { EIcon } from '../../../general-ui/icon/icon';
import { useIsSingleSession, useIsSingleSessionWithoutHome } from '../../../../hooks/session.hooks';
import { setIsNavigationVisible } from '../../../../store/actions/admin/content-actions';
import { useIsNewNavigation } from '../../../../hooks/navigation.hooks';

import './marketing-nav-v2.scss';

interface MarketingNavV2Props {
	currentChannel?: Channel;
	template: string;
	previewHide?: boolean;
	logo?: string;
	previewLanguage?: LanguagesAbbr
	showLanguageSelector?: boolean;
	disableProfileBubble?: boolean;
}

export default function MarketingNavV2({
	currentChannel,
	template,
	previewHide,
	logo,
	previewLanguage,
	showLanguageSelector = false,
	disableProfileBubble = false,
}: MarketingNavV2Props): JSX.Element | null {
	const dispatch = useDispatch();
	const eventBundle = useTypedSelector(state => state.LiveEventReducer.eventBundle);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const previewMode = useTypedSelector(state => state.CreateEventReducer.previewMode);
	const { backgroundColor, backgroundImg } = useMemo(() => {
		const workingNavbar: EventNavbar | undefined = previewMode ? workingEvent?.settings.event_navbar : eventBundle?.settings.event_navbar;
		return getEventNavbarStyling(workingNavbar?.styling_overrides);
	}, [eventBundle?.settings.event_navbar, previewMode, workingEvent?.settings.event_navbar]);

	const isNavigationOpen = useTypedSelector(state => state.ContentReducer.isNavigationOpen);
	const isDesktop = !useScreenMediaQuery().isLessThan1024;
	const isSessionPage = useIsSessionPage();
	const isSingleSession = useIsSingleSession();
	const isSingleSessionPage = useIsSingleSessionPage();
	const isSingleSessionWithoutHomePage = useIsSingleSessionWithoutHome();
	const isNewNavigation = useIsNewNavigation();

	const navbarType = eventBundle?.settings?.event_navbar?.navbarType;
	const navIconName = useGetNavIconName({ navbarType });

	const isSticky = eventBundle?.settings?.event_navbar?.isSticky;
	const isTopNavbar = navbarType === ENavbarTypes.Horizontal;
	
	const checkSessionPage = isSessionPage || (isSessionPage && isSingleSession) || isSingleSessionPage;
	const shouldShowNewNav =  isNewNavigation && isDesktop;
	const isSessionPageNewNav = isNewNavigation && isDesktop && checkSessionPage;
	const { isNavigationVisible, handleClick } = useNavigationVisibility({ shouldShowControls: isSessionPageNewNav, className: 'marketing-page-navigation' });

	useEffect(() => {
		dispatch(setIsNavigationVisible(false));
	}, [dispatch]);

	if (previewHide || !isNewNavigation) return null;

	return (
		<>
			<NavigationToggle isDesktop={isSessionPageNewNav} />

			<header
				className={classNames(
					'marketing-page-navigation',
					template,
					backgroundColor,
					{
						'preview-mode': previewMode,
						'show-navigation': isSessionPageNewNav ? isNavigationVisible : true,
						'navigation-fixed': isSessionPageNewNav ? isNavigationOpen : true,
						'navigation-toggle-active': isSessionPageNewNav || true,
						'new-navigation': shouldShowNewNav,
						'not-session-page': isNewNavigation && !isSessionPageNewNav,
						'new-top-navigation': isTopNavbar && shouldShowNewNav,
						'sticky-top-navigation': isSticky && shouldShowNewNav,
						'single-session-nav': isSingleSessionWithoutHomePage,
					})}
				style={backgroundImg}
			>
				<OptionalComponent display={isSessionPageNewNav}>
					<span className={classNames('navigation-fixed-button')} onClick={handleClick}>
						{isNavigationOpen ? (
							<Icon name={navIconName} color={COLORS.BLACK} size={20} />
						) : (
							<EIcon name='menu' size='large' className='icon-menu' />
						)}
					</span>
				</OptionalComponent>

				<EventLogo logo={logo} channel={currentChannel} />

				<nav className={classNames('navigation', { "nav-items": isDesktop })}>
					<MarketingNavMenu disableProfileBubble={disableProfileBubble} showLanguageSelector={showLanguageSelector} previewLanguage={previewLanguage} template={template} />
				</nav>
			</header>
		</>
	);
}
